import useDashboardAccess from "@/hooks/useDashboardAccess";
import { RenewalMethod, subscriptionService } from "@/services/subscription";
import { Alert, Badge, Button, Radio, Skeleton, Stack, Text } from "@mantine/core";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import classes from "@/modules/subscription/subscription.module.css";
import { jalali } from "@/modules/subscription/SubscriptionNotices";
import useSubscription from "@/modules/subscription/useSubscription";

const fa = (n: number) => n.toLocaleString("fa-IR");
const rial = (n?: number | null) => `${fa(n ?? 0)} ریال`;
const STATUS = { Active: { label: "فعال", color: "green" }, Grace: { label: "مهلت تمدید", color: "orange" }, Expired: { label: "منقضی", color: "red" } } as const;

// «اشتراک و پرداخت»: current plan, renewal (the new term starts where the current one ends) and history.
export default function SubscriptionPage() {
  const { isManager } = useDashboardAccess();
  const { subscription: s, isLoading } = useSubscription();
  const plans = useQuery({ queryKey: ["subscription", "plans"], queryFn: subscriptionService.plans, enabled: isManager });
  const history = useQuery({ queryKey: ["subscription", "history"], queryFn: subscriptionService.history, enabled: isManager });
  const [plan, setPlan] = useState<string | null>(null);
  const [method, setMethod] = useState<RenewalMethod | null>(null);
  const [paying, setPaying] = useState(false);
  useEffect(() => {
    if (!plan && plans.data?.length) setPlan((plans.data.find((p) => p.months === 12) ?? plans.data[0]).code);
  }, [plans.data, plan]);
  const options = useQuery({ queryKey: ["subscription", "options", plan], queryFn: () => subscriptionService.paymentOptions(plan!), enabled: !!plan });
  useEffect(() => {
    if (!options.data) return;
    setMethod(options.data.online?.enabled ? "Online" : options.data.snappPay?.eligible ? "SnappPay" : null);
  }, [options.data]);

  if (!isManager) return <div className={classes.page}><Alert color="gray">اشتراک سالن را مدیر یا مالک مدیریت می‌کند.</Alert></div>;
  if (isLoading) return <div className={classes.page}><Skeleton height={110} radius={14} /><Skeleton height={220} radius={14} /></div>;
  if (!s) return <div className={classes.page}><Alert color="gray">اطلاعات اشتراک فعلاً در دسترس نیست.</Alert></div>;

  const pay = async () => {
    if (!plan || !method) return;
    setPaying(true);
    try {
      const { paymentPageUrl } = await subscriptionService.renew(plan, method);
      window.location.href = paymentPageUrl;
    } catch {
      setPaying(false);
    }
  };

  return (
    <div className={classes.page}>
      <div>
        <h1>اشتراک و پرداخت</h1>
        <p className={classes.lead}>وضعیت اشتراک سالن، تمدید و سابقهٔ پرداخت‌ها.</p>
      </div>

      <section className={classes.panel}>
        <h2>اشتراک فعلی</h2>
        <div className={classes.facts}>
          <div><span>طرح</span><b>{s.planName}</b></div>
          <div><span>وضعیت</span><Badge variant="light" color={STATUS[s.status].color}>{STATUS[s.status].label}</Badge></div>
          <div><span>پایان</span><b>{jalali(s.endDate)}</b></div>
          <div><span>روز مانده</span><b>{fa(Math.max(0, s.daysLeft))}</b></div>
        </div>
        {s.status === "Grace" && <Text size="sm" c="orange.8">تا {jalali(s.graceEndDate)} همه‌چیز کار می‌کند؛ بعد از آن سالن فقط‌خواندنی می‌شود.</Text>}
      </section>

      <section className={classes.panel}>
        <h2>تمدید اشتراک</h2>
        {s.requiresContractSignature ? (
          <Alert color="orange">قرارداد پدیکس به‌روز شده و پیش از تمدید باید امضا شود. برای امضای قرارداد جدید با پشتیبانی پدیکس تماس بگیرید.</Alert>
        ) : plans.isLoading ? <Skeleton height={140} /> : plans.isError ? <Alert color="red">دریافت طرح‌ها انجام نشد.<Button variant="subtle" onClick={() => plans.refetch()}>تلاش دوباره</Button></Alert> : (
          <>
            <div className={classes.plans} role="radiogroup" aria-label="طرح اشتراک">
              {(plans.data ?? []).map((p) => (
                <button key={p.code} type="button" role="radio" aria-checked={plan === p.code} className={classes.plan} onClick={() => setPlan(p.code)}>
                  {p.badge && <span className={classes.badge}>{p.badge}</span>}
                  <strong>{p.name}</strong>
                  <span>{fa(p.months)} ماه</span>
                  {p.listPrice && p.listPrice > p.price && <s>{rial(p.listPrice)}</s>}
                  <b>{rial(p.price)}</b>
                </button>
              ))}
            </div>
            <Text size="sm" c="dimmed">مدت جدید به انتهای اشتراک فعلی اضافه می‌شود.</Text>
            {options.isLoading ? <Skeleton height={60} /> : options.data && (
              <Radio.Group label="روش پرداخت" value={method ?? ""} onChange={(m) => setMethod(m as RenewalMethod)}>
                <Stack gap="xs" mt={6}>
                  {options.data.online?.enabled && <Radio value="Online" label="پرداخت آنلاین با کارت بانکی" />}
                  <Radio value="SnappPay" disabled={!options.data.snappPay?.eligible} label={options.data.snappPay?.title || "پرداخت اقساطی اسنپ‌پی"} description={options.data.snappPay?.eligible ? options.data.snappPay?.description : "برای این مبلغ در دسترس نیست"} />
                </Stack>
              </Radio.Group>
            )}
            <div><Button color="violet" size="md" disabled={!plan || !method} loading={paying} onClick={pay}>پرداخت و تمدید</Button></div>
          </>
        )}
      </section>

      <section className={classes.panel}>
        <h2>سابقهٔ پرداخت‌ها</h2>
        {history.isLoading ? <Skeleton height={100} /> : !history.data?.length ? <Text size="sm" c="dimmed">هنوز پرداختی ثبت نشده است.</Text> : (
          <div className={classes.tableWrap}>
            <table className={classes.table}>
              <thead><tr><th>تاریخ</th><th>طرح</th><th>مبلغ</th><th>روش</th><th>وضعیت</th><th>دوره</th></tr></thead>
              <tbody>
                {history.data.map((h) => (
                  <tr key={h.id}>
                    <td>{jalali(h.paidAt)}</td>
                    <td>{plans.data?.find((p) => p.code === h.planCode)?.name ?? h.planCode}</td>
                    <td>{rial(h.amount)}</td>
                    <td>{h.method === "SnappPay" ? "اسنپ‌پی" : h.method === "Online" ? "آنلاین" : h.method}</td>
                    <td>{h.status}</td>
                    <td>{h.startDate ? `${jalali(h.startDate)} تا ${jalali(h.endDate)}` : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
